import Error  from "@/prototype/utils/Error.js";
import {hash} from "@/prototype/utils/Hash.js";

import deterministic_stringify from "@/library/stringify.js";

import global   from "@/prototype/global.js";

const log = global.log.getLogger("prototype/ConfigurationIndex");

import PQueue from "p-queue/dist/index.js";

const CONFIGURATION_CREATE = 11;
const CONFIGURATION_UPDATE = 12;
const CONFIGURATION_DELETE = 13;
const CONFIGURATION_IGNORE = 14;

// changeset state
const CHANGESET_PENDING  = 21;
const CHANGESET_WAITING  = 22;
const CHANGESET_PUSHED   = 23;
const CHANGESET_REVERTED = 24;

let changeset_prototype = {
    create: function() {
        if (this.method === CONFIGURATION_DELETE) {
            this.method = CONFIGURATION_UPDATE;
            return;
        }
        this.method = CONFIGURATION_CREATE;
    },
    update: function() {
        if (this.method === CONFIGURATION_CREATE) {
            return;
        }
        this.method = CONFIGURATION_UPDATE;
    },
    delete: function() {
        if (this.method === CONFIGURATION_CREATE) {
            this.method = CONFIGURATION_IGNORE;
            return;
        }
        this.method = CONFIGURATION_DELETE;
    }
};
let Changeset = function(name) {
    this.method = null;
    this.state = CHANGESET_PENDING;
    this.old_data = null;
    this.new_data = null;
    this.old_hash = null;
    this.new_hash = null;
    Object.defineProperty(this, "name", {
        set: () => {},
        get: () => name
    });
};
Object.assign(Changeset.prototype, changeset_prototype);
let Store = function(context) {
    let pending = Object(), pushing = Object();
    let room = null;
    Object.defineProperty(this, "room", {
        set: (new_room) => {
            if (new_room === null) {
                room = null;
                return;
            }
            room = new_room;
        },
        get: () => room
    });
    this.init = (new_room) => {
        let old_room = room;
        if (String(old_room) === String(new_room)) {
            return Promise.resolve();
        }
        pending = Object(); pushing = Object();
        room = new_room;
        if (room !== null) {
            return context.configuration_retrieve({}).modify(configuration => {
                delete configuration.hash;
                this.commit(configuration.name, null, configuration);
            });
        } else {
            return context.configuration_retrieve({}).each(configuration => {
                this.commit(configuration.name, null, configuration);
            });
        }
    };
    this.deinit = () => {
        pending = Object(); pushing = Object();
        room = null;
        return context.configuration_retrieve({}).modify(configuration => {
            delete configuration.hash;
            this.commit(configuration.name, null, configuration);
        });
    };
    this.hash = (configuration_data) => {
        if (room === null) {
            return null;
        }
        return hash({
            data: configuration_data,
            secret: room.secret
        });
    };
    this.commit = (name, old_configuration, new_configuration) => {
        let changeset = null;
        
        if (name in pushing) {
            // quick update it while we've still got some time!
            changeset = pushing[name];
            changeset.new_data = deterministic_stringify(new_configuration.data);
            changeset.new_hash = this.hash(changeset.new_data);
            pushing[name] = changeset;
        } else {
            if (name in pending) {
                changeset = pending[name];
            } else {
                changeset = new Changeset(name);
                pending[name] = changeset;
            }
            if (old_configuration === null && new_configuration !== null) {
                changeset.create();
            } else if (old_configuration !== null && new_configuration !== null) {
                if (changeset.method === CONFIGURATION_CREATE){
                    changeset.create();
                } else {
                    changeset.update();
                }
            } else if (old_configuration !== null && new_configuration === null) {
                changeset.delete();
            }
            switch(changeset.method) {
            case CONFIGURATION_CREATE:
                changeset.old_data = null;
                changeset.new_data = deterministic_stringify(new_configuration.data);
                changeset.old_hash = null;
                changeset.new_hash = this.hash(changeset.new_data);
                break;
            case CONFIGURATION_UPDATE:
                if (changeset.old_hash === null) {
                    changeset.old_data = deterministic_stringify(old_configuration.data);
                    changeset.old_hash = this.hash(changeset.old_data);
                }
                changeset.new_data = deterministic_stringify(new_configuration.data);
                changeset.new_hash = this.hash(changeset.new_data);
                break;
            case CONFIGURATION_DELETE:
                changeset.old_data = deterministic_stringify(old_configuration.data);
                changeset.new_data = null;
                changeset.old_hash = this.hash(changeset.old_data);
                changeset.new_hash = null;
                break;
            case CONFIGURATION_IGNORE:
                changeset.old_data = null;
                changeset.new_data = null;
                changeset.old_hash = null;
                changeset.new_hash = null;
                break;
            }
            if (changeset.old_hash === changeset.new_hash) {
                delete pending[name];
            }
        }
        return changeset;
    };

    const rx_tx_queue = new PQueue({concurrency: 1});

    const push = () => {
        if (room === null) {
            return Promise.resolve();
        }
        let changesets = [], updated = [], conflicts = [];
        return Promise.resolve().then(() => {
            for (let name in pending) {
                let changeset = pending[name];
                if (changeset.state !== CHANGESET_PENDING) {
                    continue;
                }
                if (changeset.new_hash === null) {
                    // recomputing hash if it has not been computed
                    changeset.new_hash = this.hash(changeset.data);
                }
                if (changeset.old_hash !== changeset.new_hash){
                    changeset.state = CHANGESET_WAITING;
                    changesets.push(changeset);
                    delete pending[changeset.name];
                    pushing[changeset.name] = changeset;
                }
            }
        }).then(() => {
            return changesets.reduce((cursor, changeset) => {
                return cursor.then(() => {
                    let method_data = {
                        space: room.space,
                        desktop: room.desktop,
                        configuration_name: changeset.name
                    };
                    switch(changeset.method) {
                    case CONFIGURATION_CREATE:
                        Object.assign(method_data, {
                            new_hash: changeset.new_hash,
                            configuration_data: changeset.new_data,
                        });
                        break;
                    case CONFIGURATION_UPDATE:
                        Object.assign(method_data, {
                            old_hash: changeset.old_hash,
                            new_hash: changeset.new_hash,
                            configuration_data: changeset.new_data
                        });
                        break;
                    case CONFIGURATION_DELETE:
                        Object.assign(method_data, {
                            old_hash: changeset.old_hash
                        });
                    }
                    return global.server.mtac_configuration_update(method_data);
                }).catch((error) => {
                    delete pushing[changeset.name];
                    changeset.state = CHANGESET_PENDING;
                    pending[changeset.name] = changeset;
                    switch(changeset.method) {
                    case CONFIGURATION_CREATE:
                        log.error(`failed to create new configuration ${changeset.name}: x => ${changeset.new_hash} on server`);
                        break;
                    case CONFIGURATION_UPDATE:
                        log.error(`failed to update configuration ${changeset.name}: ${changeset.old_hash} => ${changeset.new_hash} on server`);
                        break;
                    case CONFIGURATION_DELETE:
                        log.error(`failed to delete configuration ${changeset.name}: ${changeset.old_hash} => x on server`);
                        break;
                    }
                    throw error;
                }).then(() => {
                    delete pushing[changeset.name];
                    changeset.state = CHANGESET_PUSHED;
                    switch(changeset.method) {
                    case CONFIGURATION_CREATE:
                    case CONFIGURATION_UPDATE:
                        updated.push(context.configuration_update({
                            name: changeset.name,
                            hash: changeset.new_hash
                        }));
                        break;
                    }
                }).catch(error => {
                    switch(error.status) {
                    case Error.status.conflict:
                        conflicts.push(changeset);
                        return;
                    case Error.status.netdown:
                    case Error.status.hostdown:
                        throw error;
                    }
                    log.error("error processing configuration changeset", error);
                    throw error;
                });
            }, Promise.resolve());
        }).then(() => {
            return Promise.all(updated);
        }).catch((error) => {
            switch(error.status) {
            case Error.status.netdown:
            case Error.status.hostdown:
                return;
            case Error.status.access:
                global.storage.unshare({});
                return;
            default:
                log.error("failed to handle configuration error, will try again next time", error);
                return;
            }
        }).then(() => {
            if (conflicts.length === 0) {
                return;
            }
            throw new Error.build.conflict({
                message: "configuration update conflict",
                data: {}
            });
        });
    };
    this.push = () => rx_tx_queue.add(() => push());

    const pull = async () => {
        if (room === null) {
            return;
        }

        try {
            var configurations = await global.server.mtac_configuration_list(room).then(response => response.data.list);
        } catch (error) {
            switch(error.status) {
            case Error.status.netdown:
            case Error.status.hostdown:
                return;
            case Error.status.access:
                global.storage.unshare({});
                return;
            default:
                log.error("failed to handle configuration pull error, will try again next time", error);
                return;
            }
        }

        let local_configurations = await context.configuration_retrieve({});
        let local_configurations_names = await local_configurations.primaryKeys();

        for (let remote_configuration of configurations) {
            let local_configuration_index = local_configurations_names.indexOf(remote_configuration.name);
            if (local_configuration_index !== -1) {
                local_configurations_names.splice(local_configuration_index, 1);
                let local_configuration = await context.configuration_retrieve({name: remote_configuration.name});
                if (local_configuration.hash === remote_configuration.hash) {
                    continue;
                }
                await context.configuration_update(remote_configuration);
                continue;
            }
            await context.configuration_create(remote_configuration);
        }

        for (let name of local_configurations_names) {
            await context.configuration_delete({name, hash: true});
        }
    };
    this.pull = () => rx_tx_queue.add(() => pull());

    const revert = async () => {
        var reverted = [];
        for (let local_id in pending) {
            const changeset = pending[local_id];
            if (changeset.state !== CHANGESET_PENDING) {
                continue;
            }
            changeset.state = CHANGESET_REVERTED;
            reverted.push(global.server.mtac_configuration_retrieve({
                space: room.space,
                desktop: room.desktop,
                configuration_name: changeset.name
            }).then((response) => {
                return {
                    name: changeset.name,
                    hash: response.data.hash,
                    data: response.data.configuration_data
                };
            }).catch(error => {
                switch(error.status) {
                case Error.status.noent:
                    // configuration doesn't exist yet on server
                    return null;
                default:
                    throw error;
                }
            }).then(configuration_data => {
                if (configuration_data === null) {
                    return context.configuration_delete({name: changeset.name, hash: true});
                }
                return context.configuration_update(configuration_data);
            }));
            delete pending[changeset.name];
            delete pushing[changeset.name];
        }
        await Promise.all(reverted);
    };
    this.revert = () => rx_tx_queue.add(() => revert());
};
export default Store;